import React from 'react';
import styles from './styles.module.css';
import { IconButton } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs from 'dayjs';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import { computeDelta, formatDate, formatNumber, formatStep, formatValue, getMeasureInfo, measureSeries } from './bodyMeasurementsMath';

const CHART_HEIGHT = 220;
const CHART_MARGIN = { top: 16, right: 16, bottom: 28, left: 52 };

/** Limites do eixo com folga, para a linha não encostar nas bordas. */
function axisBounds(values, decimals) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = Math.pow(10, -decimals);
  const pad = Math.max((max - min) * 0.2, step * 10);
  return { min: Math.floor((min - pad) / step) * step, max: Math.ceil((max + pad) / step) * step };
}

/**
 * Histórico de uma medida (ou do IMC): gráfico de linha nas datas reais de
 * coleta e lista dos valores, do mais recente ao mais antigo, com a variação
 * em relação ao anterior e atalho para editar a avaliação daquela data.
 * Só entram as avaliações em que a medida foi registrada.
 */
const BodyMeasurementHistory = ({ history, measureKey, enabled, onEdit }) => {
  const info = getMeasureInfo(measureKey);
  if (!info) return null;

  const points = measureSeries(history, measureKey);
  const values = points.map((point) => point.value);
  const bounds = values.length > 0 ? axisBounds(values, info.decimals) : null;
  const rows = [...points].reverse().map((point, index, list) => {
    const previous = list[index + 1];
    return { ...point, step: previous ? formatStep(computeDelta(point.value, previous.value), info) : null };
  });

  if (points.length === 0) {
    return <span className={styles.historyHint}>Esta medida ainda não foi registrada.</span>;
  }

  return (
    <div className={styles.history}>
      {points.length < 2 ? (
        <span className={styles.historyHint}>O gráfico aparece a partir da segunda avaliação com esta medida.</span>
      ) : (
        <div className={styles.historyChart}>
          <LineChart
            height={CHART_HEIGHT}
            margin={CHART_MARGIN}
            xAxis={[{
              data: points.map((point) => dayjs(point.assessment.measuredAt).toDate()),
              scaleType: 'time',
              valueFormatter: (date) => dayjs(date).format('DD/MM/YY'),
              tickNumber: Math.min(points.length, 6),
            }]}
            yAxis={[{
              min: bounds.min,
              max: bounds.max,
              valueFormatter: (value) => formatNumber(value, info.decimals),
            }]}
            series={[{
              data: values,
              color: ColorPalette.main,
              showMark: true,
              curve: 'monotoneX',
              valueFormatter: (value) => formatValue(value, info),
            }]}
            legend={{ hidden: true }}
            grid={{ horizontal: true }}
            sx={{
              '& .MuiLineElement-root': { strokeWidth: 2.5 },
              '& .MuiMarkElement-root': { stroke: '#fff', strokeWidth: 2, r: 4, fill: ColorPalette.main },
              '& .MuiChartsAxis-tickLabel': { fontSize: '11px !important', fill: `${ColorPalette.textLight} !important` },
              '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: `${ColorPalette.border} !important` },
              '& .MuiChartsGrid-line': { stroke: `${ColorPalette.border} !important`, strokeDasharray: '3 3' },
            }}
          />
        </div>
      )}

      <div className={styles.historyList}>
        {rows.map((row, index) => (
          <div key={row.assessment.id} className={`${styles.historyRow} ${index === 0 ? styles.historyRowCurrent : ''}`}>
            <span className={styles.historyDate}>{formatDate(row.assessment.measuredAt)}</span>
            <span className={styles.historyValue}>{formatValue(row.value, info)}</span>
            <span className={styles.historyStep}>{row.step && <span className={styles.deltaPill}>{row.step}</span>}</span>
            {enabled && (
              <IconButton
                size="small"
                className={styles.editButton}
                aria-label={`Editar avaliação de ${formatDate(row.assessment.measuredAt)}`}
                onClick={() => onEdit(row.assessment)}
              >
                <EditOutlinedIcon fontSize="inherit" />
              </IconButton>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BodyMeasurementHistory;
